import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getSubscriptionsByAssociation } from "../../../../../services/subscriptionsServices";
import { createReferral } from "../../../../../services/referralServices";

import {
  DEFAULT_REFERRAL_FORM,
  calculateSubscriptionStats,
  splitSubscriptions,
  safeArray,
  validateReferralForm,
} from "../utils/subscriptionDashboardUtils";

export function useSubscriptionDashboard() {
  const associationId = process.env.REACT_APP_ASSO_ID;

  const [subscriptionsRaw, setSubscriptionsRaw] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showReferralForm, setShowReferralForm] = useState(true);
  const [referralForm, setReferralForm] = useState(DEFAULT_REFERRAL_FORM);
  const [referralErrors, setReferralErrors] = useState({});
  const [submittingReferral, setSubmittingReferral] = useState(false);

  const fetchSubscriptions = useCallback(async () => {
    if (!associationId) {
      toast.error("Association introuvable : REACT_APP_ASSO_ID manquant.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getSubscriptionsByAssociation(associationId);
      setSubscriptionsRaw(safeArray(data));
    } catch (error) {
      console.error("Erreur chargement abonnements :", error);
      toast.error("Erreur lors de la récupération des abonnements.");
      setSubscriptionsRaw([]);
    } finally {
      setLoading(false);
    }
  }, [associationId]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const { subscriptions, referrals } = useMemo(() => {
    return splitSubscriptions(subscriptionsRaw);
  }, [subscriptionsRaw]);

  const stats = useMemo(() => {
    return calculateSubscriptionStats(subscriptionsRaw);
  }, [subscriptionsRaw]);

  const handleReferralChange = (event) => {
    const { name, value } = event.target;

    setReferralForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setReferralErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const resetReferralForm = () => {
    setReferralForm(DEFAULT_REFERRAL_FORM);
    setReferralErrors({});
  };

  const submitReferral = async (event) => {
    event?.preventDefault?.();

    const errors = validateReferralForm(referralForm);
    setReferralErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.warn("Veuillez vérifier les champs du formulaire.");
      return;
    }

    try {
      setSubmittingReferral(true);

      await createReferral(referralForm);

      toast.success("Parrainage envoyé avec succès.");
      resetReferralForm();
    } catch (error) {
      console.error("Erreur envoi parrainage :", error);
      toast.error("Erreur lors de l’envoi du parrainage.");
    } finally {
      setSubmittingReferral(false);
    }
  };

  return {
    subscriptionsRaw,
    subscriptions,
    referrals,
    loading,
    stats,

    fetchSubscriptions,

    showReferralForm,
    setShowReferralForm,
    referralForm,
    referralErrors,
    submittingReferral,
    handleReferralChange,
    resetReferralForm,
    submitReferral,
  };
}